import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { MapPin, Phone, Mail, Globe, ArrowLeft, Heart, Calendar, Users, Send, Loader2, Facebook, Instagram } from "lucide-react";
import * as Icons from "lucide-react";
import { ACTIVITY_ICONS, ACTIVITY_LABELS } from "@/lib/activities";
import { entityTypeLabel } from "@/lib/entityTypes";
import FarmMap from "@/components/FarmMap";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : "");

export default function FarmDetail() {
  const { id } = useParams();
  const [farm, setFarm] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [showBooking, setShowBooking] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/companies/${id}`).then((r) => setFarm(r.data)).catch(() => navigate("/fattorie"));
  }, [id, navigate]);

  useEffect(() => {
    if (user && farm) {
      api.get("/favorites").then((r) => setIsFav(r.data.some((f) => f.id === farm.id))).catch(() => {});
    }
  }, [user, farm]);

  const toggleFav = async () => {
    if (!user) { toast.info("Accedi per salvare i preferiti"); navigate("/login"); return; }
    try {
      if (isFav) { await api.delete(`/favorites/${farm.id}`); setIsFav(false); }
      else { await api.post(`/favorites/${farm.id}`); setIsFav(true); toast.success("Aggiunto ai preferiti"); }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!farm) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Loader2 className="animate-spin text-[#4A6B53]" size={28} />
    </div>
  );

  return (
    <div className="bg-[#FBF9F6]">
      {/* Gallery */}
      <div className="relative bg-[#2C312E]">
        <div className="mx-auto max-w-7xl px-6 md:px-10 pt-6 pb-4">
          <Link to="/fattorie" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold" data-testid="back-to-list">
            <ArrowLeft size={16}/> Torna alle fattorie
          </Link>
        </div>
        <div className="mx-auto max-w-7xl px-6 md:px-10 pb-8 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 h-[340px] md:h-[480px] rounded-[2rem] overflow-hidden relative">
            <img src={imgSrc(farm.gallery?.[activeImg] || farm.cover_image)} alt={farm.name} className="w-full h-full object-cover"/>
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-1 gap-3">
            {(farm.gallery || [farm.cover_image]).slice(0, 3).map((g, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`rounded-2xl overflow-hidden h-28 lg:h-[152px] border-2 transition-all ${activeImg === i ? "border-[#C8795A]" : "border-transparent opacity-80 hover:opacity-100"}`}
                data-testid={`thumb-${i}`}
              >
                <img src={imgSrc(g)} alt="" className="w-full h-full object-cover"/>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-10 py-10 md:py-14 grid lg:grid-cols-3 gap-10">
        {/* Main */}
        <div className="lg:col-span-2 space-y-10">
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F2EBE1] text-[#4A6B53] px-3 py-1 text-xs font-bold">
                <MapPin size={12} /> {farm.city} ({farm.province}) · {farm.region}
              </span>
              {farm.distance_km !== undefined && (
                <span className="inline-flex items-center rounded-full bg-[#4A6B53] text-white px-3 py-1 text-xs font-bold">{farm.distance_km} km da te</span>
              )}
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl text-[#2C312E] leading-tight">{farm.name}</h1>
            {farm.contact_person && <p className="mt-2 text-sm text-[#58625B]">Referente: <span className="font-bold text-[#2C312E]">{farm.contact_person}</span></p>}
          </div>

          <div>
            <h2 className="font-display font-extrabold text-2xl text-[#2C312E] mb-3">Tipo di realtà</h2>
            {entityTypeLabel(farm) && (
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#4A6B53]/10 text-[#4A6B53] px-4 py-1.5 text-xs font-bold uppercase tracking-wider" data-testid="farm-entity-type">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4A6B53]"/>
                {entityTypeLabel(farm)}
              </div>
            )}
            <p className="text-[#58625B] leading-relaxed text-lg">{farm.description}</p>
          </div>

          <div>
            <h2 className="font-display font-extrabold text-2xl text-[#2C312E] mb-5">Attività proposte</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(farm.activities || []).map((a) => {
                const Icon = Icons[ACTIVITY_ICONS[a]] || Icons.Sparkles;
                return (
                  <div key={a} className="flex items-center gap-3 rounded-2xl bg-white border border-[#E5DCD0] p-4">
                    <div className="w-10 h-10 rounded-xl bg-[#F2EBE1] text-[#4A6B53] flex items-center justify-center">
                      <Icon size={18} strokeWidth={2.2} />
                    </div>
                    <span className="text-sm font-bold text-[#2C312E]">{ACTIVITY_LABELS[a] || a}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h2 className="font-display font-extrabold text-2xl text-[#2C312E] mb-4">Dove siamo</h2>
            <FarmMap farms={[farm]} height={380}/>
            <p className="mt-3 text-sm text-[#58625B]">{farm.address}, {farm.postal_code} {farm.city} ({farm.province})</p>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-28 space-y-4">
            <div className="rounded-[2rem] bg-white border border-[#E5DCD0] p-6 shadow-sm">
              <button
                onClick={() => setShowBooking(true)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#4A6B53] text-white py-4 text-base font-bold hover:bg-[#3D5844] hover:-translate-y-0.5 transition-all shadow-sm"
                data-testid="request-booking-btn"
              >
                <Calendar size={18} /> Richiedi una visita
              </button>
              <button
                onClick={toggleFav}
                className={`mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border-2 py-3 text-sm font-bold transition-all ${
                  isFav ? "border-[#C8795A] text-[#C8795A] bg-[#C8795A]/5" : "border-[#E5DCD0] text-[#58625B] hover:border-[#C8795A] hover:text-[#C8795A]"
                }`}
                data-testid="favorite-toggle-detail"
              >
                <Heart size={16} className={isFav ? "fill-[#C8795A]" : ""} />
                {isFav ? "Nei preferiti" : "Salva nei preferiti"}
              </button>
              <div className="mt-6 space-y-3 text-sm">
                {farm.phone && (
                  <a href={`tel:${farm.phone}`} className="flex items-center gap-3 text-[#2C312E] hover:text-[#C8795A]" data-testid="farm-phone">
                    <div className="w-9 h-9 rounded-xl bg-[#F2EBE1] flex items-center justify-center text-[#4A6B53]"><Phone size={15}/></div>
                    <span className="font-semibold">{farm.phone}</span>
                  </a>
                )}
                {farm.email && (
                  <a href={`mailto:${farm.email}`} className="flex items-center gap-3 text-[#2C312E] hover:text-[#C8795A] break-all" data-testid="farm-email">
                    <div className="w-9 h-9 rounded-xl bg-[#F2EBE1] flex items-center justify-center text-[#4A6B53] shrink-0"><Mail size={15}/></div>
                    <span className="font-semibold">{farm.email}</span>
                  </a>
                )}
                {farm.website && (
                  <a href={farm.website.startsWith("http") ? farm.website : `https://${farm.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-[#2C312E] hover:text-[#C8795A] break-all" data-testid="farm-website">
                    <div className="w-9 h-9 rounded-xl bg-[#F2EBE1] flex items-center justify-center text-[#4A6B53] shrink-0"><Globe size={15}/></div>
                    <span className="font-semibold">{farm.website}</span>
                  </a>
                )}
              </div>
              {(farm.facebook || farm.instagram) && (
                <div className="mt-5 pt-5 border-t border-[#E5DCD0]">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#7A7369] mb-3">Seguici sui social</div>
                  <div className="flex gap-2">
                    {farm.facebook && (
                      <a
                        href={farm.facebook.startsWith("http") ? farm.facebook : `https://${farm.facebook.replace(/^@/, "facebook.com/")}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-[#E5DCD0] px-4 py-2 text-sm font-bold text-[#1877F2] hover:border-[#1877F2] transition-all"
                        data-testid="farm-facebook"
                      >
                        <Facebook size={16}/> Facebook
                      </a>
                    )}
                    {farm.instagram && (
                      <a
                        href={farm.instagram.startsWith("http") ? farm.instagram : `https://instagram.com/${farm.instagram.replace(/^@/, "")}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-[#E5DCD0] px-4 py-2 text-sm font-bold text-[#E1306C] hover:border-[#E1306C] transition-all"
                        data-testid="farm-instagram"
                      >
                        <Instagram size={16}/> Instagram
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      {showBooking && <BookingModal farm={farm} onClose={() => setShowBooking(false)} user={user}/>}
    </div>
  );
}

function BookingModal({ farm, onClose, user }) {
  const [form, setForm] = useState({
    visitor_name: user?.name || "",
    visitor_email: user?.email || "",
    visitor_phone: "",
    visitor_type: user?.user_type === "scuola" ? "scuola" : "famiglia",
    group_size: 10,
    preferred_date: "",
    activities: [],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/bookings", { ...form, company_id: farm.id });
      toast.success("Richiesta inviata! La fattoria ti contatterà al più presto.");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  const toggleAct = (a) => setForm((f) => ({ ...f, activities: f.activities.includes(a) ? f.activities.filter((x) => x !== a) : [...f.activities, a] }));

  return (
    <div className="fixed inset-0 z-50 bg-[#2C312E]/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl md:rounded-[2rem] rounded-t-[2rem] p-6 md:p-10 my-0 md:my-8" data-testid="booking-form">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#C8795A]">Richiedi una visita</div>
            <h3 className="font-display font-black text-2xl md:text-3xl text-[#2C312E] mt-1">{farm.name}</h3>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full border-2 border-[#E5DCD0] flex items-center justify-center hover:border-[#C8795A]">✕</button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">Chi sei?</span>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {["famiglia", "scuola"].map((t) => (
                <button type="button" key={t} onClick={() => setForm({ ...form, visitor_type: t })}
                  className={`rounded-xl border-2 py-3 text-sm font-bold capitalize ${form.visitor_type === t ? "border-[#4A6B53] bg-[#4A6B53] text-white" : "border-[#E5DCD0] text-[#58625B]"}`}
                  data-testid={`visitor-type-${t}`}>
                  {t === "famiglia" ? "Famiglia" : "Scuola"}
                </button>
              ))}
            </div>
          </label>
          <Field label="Numero di persone" type="number" value={form.group_size} onChange={(v) => setForm({ ...form, group_size: Number(v) })} min={1} testid="group-size"/>
          <Field label="Nome e Cognome / Scuola" value={form.visitor_name} onChange={(v) => setForm({ ...form, visitor_name: v })} required testid="visitor-name"/>
          <Field label="Email" type="email" value={form.visitor_email} onChange={(v) => setForm({ ...form, visitor_email: v })} required testid="visitor-email"/>
          <Field label="Telefono" value={form.visitor_phone} onChange={(v) => setForm({ ...form, visitor_phone: v })} required testid="visitor-phone"/>
          <Field label="Data preferita" type="date" value={form.preferred_date} onChange={(v) => setForm({ ...form, preferred_date: v })} required testid="preferred-date"/>
        </div>

        <div className="mt-4">
          <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">Attività di interesse</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {(farm.activities || []).map((a) => (
              <button type="button" key={a} onClick={() => toggleAct(a)}
                className={`rounded-full border px-3 py-1.5 text-xs font-bold ${form.activities.includes(a) ? "bg-[#C8795A] text-white border-[#C8795A]" : "bg-white border-[#E5DCD0] text-[#58625B]"}`}
                data-testid={`booking-act-${a}`}>
                {ACTIVITY_LABELS[a]}
              </button>
            ))}
          </div>
        </div>

        <label className="block mt-4">
          <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">Messaggio per la fattoria</span>
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4}
            className="mt-2 w-full rounded-2xl border-2 border-[#E5DCD0] bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#4A6B53] focus:border-transparent"
            placeholder="Raccontaci il tuo progetto, l'età dei bambini, particolari esigenze…"
            data-testid="booking-message"/>
        </label>

        <button type="submit" disabled={submitting}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#4A6B53] text-white py-4 font-bold hover:bg-[#3D5844] disabled:opacity-60 transition-all"
          data-testid="booking-submit">
          {submitting ? <Loader2 size={18} className="animate-spin"/> : <Send size={16}/>} Invia richiesta
        </button>
        <p className="mt-3 text-xs text-[#7A7369] text-center">La richiesta sarà inoltrata alla fattoria che ti contatterà via email o telefono.</p>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, min, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} min={min}
        className="mt-2 w-full h-12 rounded-xl border-2 border-[#E5DCD0] bg-white px-4 text-base focus:outline-none focus:ring-2 focus:ring-[#4A6B53] focus:border-transparent"
        data-testid={testid}/>
    </label>
  );
}
